// Lectura del historial del paciente para las curvas del PDF firmado de los controles de niño (016B/016C/028A-1/028B/028C). Corre DESPUÉS de guardar:
// las mediciones ya están en el expediente, así que cualquier fallo (red, permisos, cuelgue) deja el PDF sin curvas pero sin error — reintentar el
// guardado duplicaría las mediciones.
import type { Observation, Patient, Reference } from '@medplum/fhirtypes';
import type { MedplumClient } from '@medplum/core';
import type { DatosCurvasCrecimientoPdf } from '@logiacare/hce-pdf';
import { PERIMETRO_CEFALICO_LOINC, PESO_LOINC, TALLA_LOINC } from './curvaPercentilPorEdad';
import { datosCurvasControlNinoPdf, type FamiliaCurvasControlNino, type TextosCurvasControlNinoPdf } from './curvasControlNinoPdf';

/** Espera máxima de la lectura posterior al guardado: pasado el plazo el PDF sale sin curvas (el botón de guardar no se queda colgado). */
export const PLAZO_LECTURA_CURVAS_MS = 8000;

export async function buscarCurvasControlNinoPdf<F extends FamiliaCurvasControlNino>(
  medplum: Pick<MedplumClient, 'readReference' | 'searchResources'>,
  subject: Reference<Patient>,
  familia: F,
  textos: TextosCurvasControlNinoPdf<F>,
  plazoMs: number = PLAZO_LECTURA_CURVAS_MS,
): Promise<DatosCurvasCrecimientoPdf | undefined> {
  let temporizador: ReturnType<typeof setTimeout> | undefined;
  const plazo = new Promise<undefined>((resolver) => {
    temporizador = setTimeout(() => resolver(undefined), plazoMs);
  });
  const lectura = (async (): Promise<DatosCurvasCrecimientoPdf | undefined> => {
    const [paciente, historial] = await Promise.all([
      medplum.readReference(subject) as Promise<Patient>,
      medplum.searchResources('Observation', {
        patient: subject.reference ?? '',
        code: `http://loinc.org|${PESO_LOINC},http://loinc.org|${TALLA_LOINC},http://loinc.org|${PERIMETRO_CEFALICO_LOINC}`,
        'status:not': 'entered-in-error',
        _sort: '-date',
        _count: 500,
      }) as Promise<Observation[]>,
    ]);
    return datosCurvasControlNinoPdf(familia, paciente, historial, textos);
  })().catch(() => undefined);
  try {
    return await Promise.race([lectura, plazo]);
  } finally {
    clearTimeout(temporizador);
  }
}
